import { useMedplum } from '@medplum/react-hooks';
import {
  Alert,
  Anchor,
  Box,
  Button,
  Center,
  Group,
  Paper,
  PasswordInput,
  Stack,
  Text,
  Title,
} from '@mantine/core';
import { useForm } from '@mantine/form';
import { zod4Resolver } from 'mantine-form-zod-resolver';
import { IconAlertCircle, IconArrowLeft, IconCheck, IconShieldCheck } from '@tabler/icons-react';
import { useCallback, useMemo, useState } from 'react';
import { Link, useSearchParams } from 'react-router-dom';
import {
  setPasswordFormSchema,
  type SetPasswordFormValues,
} from '../../schemas/auth.schema';
import { getSetPasswordBadRequestMessage } from './setPasswordErrors';

function isSafeReturnTo(value: string): boolean {
  return value.startsWith('/') && !value.startsWith('//');
}

export function SetPasswordPage(): JSX.Element {
  const medplum = useMedplum();
  const [searchParams] = useSearchParams();

  const id = searchParams.get('id') ?? '';
  const secret = searchParams.get('secret') ?? '';
  const email = searchParams.get('email') ?? '';
  const returnTo = searchParams.get('returnTo') ?? '';
  const variant = searchParams.get('variant');
  const isReset = variant === 'reset' || document.referrer.includes('/forgot-password');

  const missingToken = !id || !secret;

  const [loading, setLoading] = useState(false);
  const [success, setSuccess] = useState(false);
  const [error, setError] = useState<string | null>(null);

  const form = useForm<SetPasswordFormValues>({
    initialValues: { password: '', confirmPassword: '' },
    validate: zod4Resolver(setPasswordFormSchema),
  });

  const loginHref = useMemo(() => {
    if (returnTo && isSafeReturnTo(returnTo)) {
      return `/signin?returnTo=${encodeURIComponent(returnTo)}`;
    }
    return '/signin';
  }, [returnTo]);

  const handleSubmit = useCallback(
    async (values: SetPasswordFormValues) => {
      if (missingToken) return;
      setLoading(true);
      setError(null);

      try {
        const baseUrl = medplum.getBaseUrl();
        const res = await fetch(`${baseUrl}auth/setpassword`, {
          method: 'POST',
          headers: { 'Content-Type': 'application/json' },
          body: JSON.stringify({ id, secret, password: values.password }),
        });
        if (!res.ok) {
          const body = await res.json().catch(() => ({}));
          const detail = (body as { issue?: Array<{ details?: { text?: string } }> })
            ?.issue?.[0]?.details?.text;
          throw Object.assign(new Error(detail ?? res.statusText), { status: res.status });
        }
        setSuccess(true);
        form.reset();
      } catch (err) {
        const status = (err as { status?: number })?.status;
        const message = (err as Error)?.message;
        if (message === 'Already used' || message === 'Expired') {
          const expired = message === 'Expired';
          setError(
            isReset
              ? 'This reset link has expired or was already used. Reset links are valid for 15 minutes after the email is sent. Please request a new one from the "Forgot password" page.'
              : expired
                ? 'This activation link has expired. Ask your practice admin to resend the invite, or use "Forgot password" to get a new link.'
                : 'This activation link has already been used. Please request a new invite or use "Forgot password".',
          );
        } else if (message && status === 400) {
          // Only Medplum's known password-policy messages are meant for the user; any other 400 text stays hidden.
          setError(getSetPasswordBadRequestMessage(err, message, isReset));
        } else if (status === 400 || status === 401 || status === 404) {
          setError(
            isReset
              ? 'This reset link is invalid or expired. Please request a new one.'
              : 'This activation link is invalid or expired. Please request a new invite or contact support.',
          );
        } else {
          setError(
            isReset
              ? "We couldn't reset your password. Please try again."
              : "We couldn't activate your account. Please try again.",
          );
        }
      } finally {
        setLoading(false);
      }
    },
    [medplum, id, secret, missingToken, form, isReset],
  );

  return (
    <Box
      bg="unifyGold.0"
      style={{
        position: 'fixed',
        inset: 0,
        width: '100vw',
        height: '100vh',
        minHeight: '100dvh',
        boxSizing: 'border-box',
      }}
    >
      <Center h="100%" p="md">
        <Paper shadow="md" p="lg" radius="md" w={400} maw="calc(100vw - 2rem)" bg="white">
          <Stack gap="md">
            <Box ta="center">
              <Title order={2} c="unifyGold.6">
                {success
                  ? (isReset ? 'Password updated' : 'Account activated')
                  : (isReset ? 'Reset your password' : 'Create your password')}
              </Title>
              {!success && (
                <Text c="dimmed" size="sm" mt="xs">
                  {isReset
                    ? 'Choose a new password for your Unify account. For your security, reset links expire 15 minutes after the email is sent.'
                    : 'Set a secure password to activate your Unify account.'}
                </Text>
              )}
              {email && !success && (
                <Text size="sm" fw={500} mt={4}>
                  {email}
                </Text>
              )}
            </Box>

            {missingToken && !success && (
              <Alert icon={<IconAlertCircle size={16} />} color="red" title="Invalid link">
                {isReset
                  ? 'This reset link is invalid or expired. Please request a new one from the "Forgot password" page.'
                  : 'This activation link is invalid or expired. Please request a new invite or contact support.'}
              </Alert>
            )}

            {error && (
              <Alert icon={<IconAlertCircle size={16} />} color="red" title="Error">
                {error}
              </Alert>
            )}

            {success ? (
              <Stack gap="md">
                <Alert icon={<IconCheck size={16} />} color="green" title="Password set successfully">
                  {isReset
                    ? 'Your password has been updated. You can now sign in.'
                    : 'Your account is ready. You can now sign in with your new password.'}
                </Alert>
                <Button
                  component={Link}
                  to={loginHref}
                  fullWidth
                  size="md"
                  leftSection={<IconShieldCheck size={20} />}
                >
                  Continue to sign in
                </Button>
              </Stack>
            ) : (
              !missingToken && (
                <form onSubmit={form.onSubmit(handleSubmit)}>
                  <Stack gap="sm">
                    <PasswordInput
                      label="New password"
                      placeholder="Enter a password"
                      autoComplete="new-password"
                      aria-label="New password"
                      {...form.getInputProps('password')}
                    />
                    <PasswordInput
                      label="Confirm password"
                      placeholder="Re-enter your password"
                      autoComplete="new-password"
                      aria-label="Confirm password"
                      {...form.getInputProps('confirmPassword')}
                    />
                    <Button
                      type="submit"
                      fullWidth
                      size="md"
                      loading={loading}
                      leftSection={<IconShieldCheck size={20} />}
                    >
                      {isReset ? 'Reset password' : 'Activate account'}
                    </Button>
                  </Stack>
                </form>
              )
            )}

            {!success && (
              <Center>
                <Anchor component={Link} to="/signin" size="sm" c="dimmed">
                  <Group gap={4}>
                    <IconArrowLeft size={14} />
                    Back to sign in
                  </Group>
                </Anchor>
              </Center>
            )}
          </Stack>
        </Paper>
      </Center>
    </Box>
  );
}
